import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BACKEND_URL } from '@/lib/backendUrl';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { productsAPI, customersAPI, salesAPI, pixAPI, paymentSettingsAPI, paymentMethodsAPI, balancaAPI, api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SimpleModal } from '@/components/SimpleModal';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/Toast';
import { CaixaModal } from '@/components/CaixaModal';
import { SangriaSuprimentoModal } from '@/components/SangriaSuprimentoModal';
import { OfflineIndicator } from '@/components/OfflineIndicator';
import { useOfflineMode } from '@/hooks/useOfflineMode';
import { Search, Trash2, User, ShoppingCart, Settings, Percent, DollarSign, FileText, X, Check, Monitor, Users, LogOut, Home, Printer, Loader2, Copy, Scale, Wallet, Clock, AlertTriangle, ArrowDownCircle, ArrowUpCircle, Wifi, WifiOff, ChevronDown } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import balancaService from '@/lib/balancaService';

const API_URL = BACKEND_URL;

/**
 * Safely extract a displayable error message from an axios error.
 * Handles FastAPI 422 (detail is array of objects with ctx.duration etc),
 * detail as object, detail as string, or generic errors.
 */
const extractErrorMessage = (error, fallback = 'Erro inesperado') => {
  const detail = error?.response?.data?.detail;
  if (!detail) return error?.message || fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (typeof d === 'string' ? d : (d?.msg || JSON.stringify(d))))
      .join('; ');
  }
  if (typeof detail === 'object') {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return String(detail);
};

export const POSPage = () => {
  const toast = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cart, setCart] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [completingSale, setCompletingSale] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [lastSale, setLastSale] = useState(null);
  const [globalDiscount, setGlobalDiscount] = useState(0);
  const [discountInput, setDiscountInput] = useState('');
  const [selectedState, setSelectedState] = useState('SP');
  const [emitirNota, setEmitirNota] = useState(true);
  const [tipoDocumento, setTipoDocumento] = useState('nfce'); // 'nfce' ou 'nfe'
  const [customerSearch, setCustomerSearch] = useState('');
  
  // Caixa
  const [caixaAtual, setCaixaAtual] = useState(null);
  const [caixaLoading, setCaixaLoading] = useState(true);
  const [showCaixaModal, setShowCaixaModal] = useState(false);
  const [tipoCaixaModal, setTipoCaixaModal] = useState('abertura');
  const [showSangriaModal, setShowSangriaModal] = useState(false);
  
  // PIX / Mercado Pago
  const [showPixModal, setShowPixModal] = useState(false);
  const [pixData, setPixData] = useState(null);
  const [pixLoading, setPixLoading] = useState(false);
  const [pixChecking, setPixChecking] = useState(false);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState([]);
  
  // Balança
  const [balancaConnected, setBalancaConnected] = useState(false);
  const [balancaName, setBalancaName] = useState('');
  const [balancaError, setBalancaError] = useState(false);  // Flag para erro na balança
  const [pesoAtual, setPesoAtual] = useState(0);
  const [showBalancaModal, setShowBalancaModal] = useState(false);
  const [balancaTipo, setBalancaTipo] = useState('generica');
  const [balancaConfig, setBalancaConfig] = useState(null); // Config do banco
  
  // Dados da empresa (cabeçalho do cupom)
  const [companyInfo, setCompanyInfo] = useState({});
  
  // Configurações do PDV (do localStorage)
  const [pdvConfig, setPdvConfig] = useState({
    mostrarEstoque: true,
    permitirVendaNegativa: false
  });
  
  // Pesquisa de produtos com dropdown
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedDropdownIndex, setSelectedDropdownIndex] = useState(0);
  const dropdownRef = useRef(null);
  
  // Modo de preço: varejo, atacado, noturno
  const [modoPreco, setModoPreco] = useState('varejo');
  
  // Hook de modo offline
  const { 
    isOnline, 
    isInitialized: offlineReady, 
    syncStatus, 
    syncNow, 
    getProducts: getOfflineProducts, 
    getProductByBarcode: getOfflineProductByBarcode,
    getCustomers: getOfflineCustomers,
    saveSale: saveOfflineSale,
    cacheProducts,
    cacheCustomers
  } = useOfflineMode();
  
  // Campos do produto atual
  const [barcodeSearch, setBarcodeSearch] = useState('');
  const [currentProduct, setCurrentProduct] = useState(null);
  const [quantity, setQuantity] = useState('1');
  const [itemDiscount, setItemDiscount] = useState('0');
  
  // Refs para controle de foco - FLUXO COM ENTER
  const barcodeInputRef = useRef(null);
  const quantityInputRef = useRef(null);
  
  const [paymentMethods, setPaymentMethods] = useState([
    { method: 'dinheiro', amount: 0 }
  ]);

  // Focar no campo de código de barras ao carregar
  useEffect(() => {
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
    
    // Carregar configurações do PDV do localStorage
    const savedPdvConfig = localStorage.getItem('config_pdv');
    if (savedPdvConfig) {
      try {
        const config = JSON.parse(savedPdvConfig);
        setPdvConfig(prev => ({...prev, ...config}));
        console.log('Configurações PDV carregadas:', config);
      } catch (e) {
        console.error('Erro ao carregar config PDV:', e);
      }
    }
    
    // Handler global para erros não tratados da balança
    const handleUnhandledRejection = (event) => {
      console.error('Unhandled promise rejection:', event.reason);
      // Previne que o erro cause tela preta
      event.preventDefault();
    };
    
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    // Carregar dados da empresa para o cupom
    (async () => {
      try {
        const resp = await api.get('/settings/company');
        if (resp?.data) setCompanyInfo(resp.data || {});
      } catch (e) { /* silencioso — cupom usa fallback */ }
    })();
    
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Carregar configuração da balança e conectar automaticamente
  useEffect(() => {
    const loadBalancaConfig = async () => {
      try {
        const response = await balancaAPI.get();
        const config = response.data;
        setBalancaConfig(config);
        
        if (config.habilitada && config.auto_conectar) {
          // Tenta conexão automática (silenciosa)
          const result = await balancaService.autoConnect(config.tipo, config.baud_rate);
          if (result.success) {
            console.log('Balança conectada automaticamente');
          } else {
            console.log('Auto-conexão falhou:', result.reason);
            // Não mostra erro para o usuário, apenas marca flag
            if (result.reason !== 'no_authorized_ports') {
              setBalancaError(true);
            }
          }
        }
      } catch (error) {
        console.log('Erro ao carregar config balança:', error);
      }
    };
    
    loadBalancaConfig();
  }, []);

  // Ref para currentProduct (para usar nos callbacks sem re-criar)
  const currentProductRef = useRef(null);
  const saleIdempotencyKeyRef = useRef(null);
  useEffect(() => {
    currentProductRef.current = currentProduct;
  }, [currentProduct]);

  // Configurar callbacks da balança (apenas uma vez)
  useEffect(() => {
    balancaService.onWeightChange = (peso) => {
      try {
        setPesoAtual(peso);
        setBalancaError(false); // Leitura OK, remove erro
        // Se tem produto selecionado e é vendido por peso, atualiza quantidade
        const prod = currentProductRef.current;
        if (prod && prod.unidade === 'KG') {
          setQuantity(peso.toFixed(3));
        }
      } catch (err) {
        console.error('Erro ao processar peso:', err);
      }
    };
    
    balancaService.onConnectionChange = (connected, name) => {
      try {
        setBalancaConnected(connected);
        setBalancaName(name || '');
        if (!connected) {
          // Não seta erro quando é desconexão intencional
          // O erro só é setado quando a conexão é perdida inesperadamente
          setPesoAtual(0);
        } else {
          setBalancaError(false);
        }
      } catch (err) {
        console.error('Erro ao atualizar conexão:', err);
      }
    };
    
    balancaService.onError = (errorMsg) => {
      try {
        setBalancaError(true);
        console.log('Erro na balança:', errorMsg);
      } catch (err) {
        console.error('Erro ao processar erro da balança:', err);
      }
    };
    
    return () => {
      balancaService.disconnect();
    };
  }, []); // Sem dependências - callbacks configurados apenas uma vez

  // Conectar balança manualmente
  const conectarBalanca = async () => {
    try {
      const tipo = balancaConfig?.tipo || balancaTipo;
      const baudRate = balancaConfig?.baud_rate || 9600;
      await balancaService.connect(tipo, baudRate);
      setShowBalancaModal(false);
      setBalancaError(false);
      toast.success(`Balança ${balancaService.currentConfig.name} conectada!`);
    } catch (error) {
      setBalancaError(true);
      toast.error('Erro ao conectar balança: ' + error.message);
    }
  };

  // Desconectar balança
  const desconectarBalanca = async () => {
    try {
      await balancaService.disconnect();
      setBalancaConnected(false);
      setBalancaName('');
      setPesoAtual(0);
      toast.info('Balança desconectada');
    } catch (error) {
      console.error('Erro ao desconectar balança:', error);
      // Garante que o estado é atualizado mesmo com erro
      setBalancaConnected(false);
      setBalancaName('');
      setPesoAtual(0);
    }
  };

  // Verifica se pode digitar peso manualmente
  const canEditWeight = useCallback(() => {
    // Só permite editar se: não tem balança configurada, ou não está conectada, ou tem erro
    if (!balancaConfig?.habilitada) return true;
    if (!balancaConnected) return true;
    if (balancaError) return true;
    return false;
  }, [balancaConfig, balancaConnected, balancaError]);

  // Carregar status do caixa
  const loadCaixa = useCallback(async () => {
    setCaixaLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/caixa/atual`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.aberto && data.caixa) {
        setCaixaAtual(data.caixa);
      } else {
        setCaixaAtual(null);
        // Se for caixa e não tiver caixa aberto, mostrar modal
        if (user?.role === 'caixa') {
          setTipoCaixaModal('abertura');
          setShowCaixaModal(true);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar caixa:', error);
    } finally {
      setCaixaLoading(false);
    }
  }, [user?.role]);

  useEffect(() => {
    loadProducts();
    loadCustomers();
    loadPaymentSettings();
    loadCaixa();
  }, [loadCaixa]);
  
  const loadPaymentSettings = async () => {
    try {
      const response = await paymentSettingsAPI.get();
      setPaymentSettings(response.data);
    } catch (error) {
      console.log('Erro ao carregar configurações de pagamento');
    }
    try {
      const { data } = await paymentMethodsAPI.list(true);
      const options = (data.payment_methods || []).map((m) => ({ value: m.code, label: m.label }));
      if (options.length) setPaymentMethodOptions(options);
    } catch (error) {
      console.log('Erro ao carregar formas de pagamento');
    }
  };

  // Callback para abertura/fechamento de caixa
  const handleCaixaSuccess = (result) => {
    if (tipoCaixaModal === 'abertura') {
      setCaixaAtual(result);
      toast.success('Caixa aberto com sucesso!');
    } else {
      setCaixaAtual(null);
      toast.success(
        result?.diferenca === undefined
          ? 'Caixa fechado com sucesso!'
          : `Caixa fechado! ${result.vendas_count} vendas, diferença: ${formatCurrency(result.diferenca)}`
      );
    }
  };

  // Callback para sangria/suprimento
  const handleSangriaSuccess = (result) => {
    toast.success(result.message);
    loadCaixa(); // Recarregar dados do caixa
  };

  // Abrir caixa
  const handleAbrirCaixa = () => {
    setTipoCaixaModal('abertura');
    setShowCaixaModal(true);
  };

  // Fechar caixa
  const handleFecharCaixa = () => {
    setTipoCaixaModal('fechamento');
    setShowCaixaModal(true);
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'F5') {
        e.preventDefault();
        if (cart.length > 0) handlePayment();
      } else if (e.key === 'F7') {
        e.preventDefault();
        setShowDiscountModal(true);
      } else if (e.key === 'F10') {
        e.preventDefault();
        if (cart.length > 0 && window.confirm('Cancelar venda atual?')) {
          setCart([]);
          setGlobalDiscount(0);
          setSelectedCustomer(null);
          toast.info('Venda cancelada');
        }
      } else if (e.key === 'F11') {
        e.preventDefault();
        setShowCustomerModal(true);
      } else if (e.key === 'F12') {
        e.preventDefault();
        if (caixaAtual) {
          handleFecharCaixa();
        } else {
          handleAbrirCaixa();
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [cart, caixaAtual]);

  const loadProducts = async () => {
    try {
      // Se offline e banco local pronto, buscar localmente
      if (!isOnline && offlineReady) {
        const localProducts = await getOfflineProducts();
        if (localProducts && localProducts.length > 0) {
          setProducts(localProducts);
          console.log('[PDV] Usando produtos do cache local:', localProducts.length);
          return;
        }
      }
      
      // Online: buscar da API
      const response = await productsAPI.getAll();
      setProducts(response.data);
      
      // Salvar no cache local para uso offline
      if (offlineReady && response.data) {
        cacheProducts(response.data);
      }
    } catch (error) {
      // Se erro e temos cache local, usar
      if (offlineReady) {
        const localProducts = await getOfflineProducts();
        if (localProducts && localProducts.length > 0) {
          setProducts(localProducts);
          toast.warning('Usando produtos salvos localmente');
          return;
        }
      }
      toast.error('Erro ao carregar produtos');
    }
  };

  const loadCustomers = async () => {
    try {
      // Se offline e banco local pronto, buscar localmente
      if (!isOnline && offlineReady) {
        const localCustomers = await getOfflineCustomers();
        if (localCustomers && localCustomers.length > 0) {
          setCustomers(localCustomers);
          return;
        }
      }
      
      // Online: buscar da API
      const response = await customersAPI.getAll();
      setCustomers(response.data);
      
      // Salvar no cache local
      if (offlineReady && response.data) {
        cacheCustomers(response.data);
      }
    } catch (error) {
      // Se erro e temos cache local, usar
      if (offlineReady) {
        const localCustomers = await getOfflineCustomers();
        if (localCustomers && localCustomers.length > 0) {
          setCustomers(localCustomers);
          return;
        }
      }
      console.error('Erro ao carregar clientes');
    }
  };

  // Função para obter o preço correto baseado no modo e quantidade
  const getPrecoAtual = (product, qty = 1) => {
    if (!product) return 0;
    
    // Se modo noturno e tem preço noturno
    if (modoPreco === 'noturno' && product.preco_noturno) {
      return product.preco_noturno;
    }
    
    // Se modo atacado ou quantidade >= mínimo atacado
    const qtdMinAtacado = product.qtd_minima_atacado || 6;
    if ((modoPreco === 'atacado' || qty >= qtdMinAtacado) && product.preco_atacado) {
      return product.preco_atacado;
    }
    
    // Preço varejo padrão
    return product.price;
  };

  // Função para decodificar código de barras de balança
  // Formato padrão brasileiro: 2CCCCPPPPPD (13 dígitos)
  // 2 = Prefixo de produto pesado
  // CCCC = Código do produto (4-6 dígitos dependendo do formato)
  // PPPPP = Peso em gramas (5 dígitos) ou preço
  // D = Dígito verificador
  const parseBalancaBarcode = (code) => {
    // Remove espaços
    const barcode = code.trim();
    
    // Verifica se é código de balança (começa com 2 e tem 13 dígitos)
    if (barcode.length === 13 && barcode.startsWith('2')) {
      // Formato mais comum: 2CCCCPPPPPD
      // Código do produto: posições 1-4 (ou 1-5)
      // Peso: posições 5-9 (ou 6-10) em gramas
      const codigoProduto = barcode.substring(1, 5); // 4 dígitos
      const pesoGramas = parseInt(barcode.substring(7, 12)); // 5 dígitos
      const pesoKg = pesoGramas / 1000;
      
      return {
        isBalanca: true,
        codigoProduto: codigoProduto,
        codigoProdutoAlt: barcode.substring(1, 6), // 5 dígitos alternativo
        peso: pesoKg,
        pesoGramas: pesoGramas
      };
    }
    
    // Também verifica formato com prefixo 02
    if (barcode.length === 13 && barcode.startsWith('02')) {
      const codigoProduto = barcode.substring(2, 6); // 4 dígitos
      const pesoGramas = parseInt(barcode.substring(6, 11));
      const pesoKg = pesoGramas / 1000;
      
      return {
        isBalanca: true,
        codigoProduto: codigoProduto,
        codigoProdutoAlt: barcode.substring(2, 7),
        peso: pesoKg,
        pesoGramas: pesoGramas
      };
    }
    
    return { isBalanca: false };
  };

  // Filtra produtos conforme digitação (para dropdown)
  const filterProducts = useCallback((searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setFilteredProducts([]);
      setShowProductDropdown(false);
      return;
    }
    
    const term = searchTerm.toLowerCase();
    const filtered = products.filter(p => 
      p.name.toLowerCase().includes(term) ||
      p.barcode?.includes(term) ||
      p.sku?.includes(term) ||
      p.codigo_balanca?.includes(term)
    ).slice(0, 10); // Limita a 10 resultados
    
    setFilteredProducts(filtered);
    setShowProductDropdown(filtered.length > 0);
    setSelectedDropdownIndex(0);
  }, [products]);

  // Seleciona produto do dropdown
  const selectProductFromDropdown = useCallback((product) => {
    try {
      if (!product) {
        console.warn('Produto inválido selecionado');
        return;
      }
      
      setCurrentProduct(product);
      setBarcodeSearch('');
      setShowProductDropdown(false);
      setFilteredProducts([]);
      
      // Produto vendido por peso: precisa da balança
      if (product.unidade === 'KG') {
        // 🎯 Lê peso DIRETO do serviço (fonte da verdade) — evita valor obsoleto
        // do state do React em situações de race condition entre leitura da
        // balança e clique/Enter do usuário.
        const pesoNow = Number(balancaService?.lastWeight) || pesoAtual || 0;
        if (pesoNow > 0) {
          // Balança lida → usa o peso independente de balancaConnected (pode ter
          // acabado de reconectar). Preenche quantidade e adiciona ao carrinho
          // automaticamente se o produto for por peso.
          setQuantity(pesoNow.toFixed(3));
          toast.success(`Peso da balança: ${pesoNow.toFixed(3)} kg`);
        } else if (balancaConnected) {
          // Balança conectada mas sem peso → orienta o operador
          setQuantity('0');
          toast.warning('Coloque o produto na balança — o peso será lido automaticamente', {
            duration: 4000,
          });
        } else {
          // Balança NÃO conectada → mostra erro claro e mantém quantidade em 0
          // (bloqueia adicionar acidentalmente 1 kg genérico)
          setQuantity('0');
          toast.error(
            'Produto vendido por KG mas a balança não está configurada. Vá em Configurações → Balança para configurar, ou digite o peso manualmente.',
            { duration: 6000 }
          );
        }
      } else {
        setQuantity('1');
      }
      
      // Foca no campo de quantidade
      setTimeout(() => {
        if (quantityInputRef.current) {
          quantityInputRef.current.focus();
          quantityInputRef.current.select();
        }
      }, 100);
    } catch (error) {
      console.error('Erro ao selecionar produto:', error);
      toast.error('Erro ao selecionar produto');
    }
  }, [balancaConnected, pesoAtual]);

  // FLUXO COM ENTER:
  // 1. Digita código/nome → mostra dropdown → seta/Enter seleciona
  // 2. Altera quantidade (ou não) → Enter → adiciona ao carrinho e volta pro código
  
  const searchByBarcode = useCallback(() => {
    if (!barcodeSearch.trim()) return;
    
    const codigo = barcodeSearch.trim();
    
    // Verifica se é código de balança
    const balancaInfo = parseBalancaBarcode(codigo);
    
    let product = null;
    let quantidade = 1;
    
    if (balancaInfo.isBalanca) {
      // Busca pelo código interno da balança
      product = products.find(p => 
        p.barcode === balancaInfo.codigoProduto ||
        p.barcode === balancaInfo.codigoProdutoAlt ||
        p.sku === balancaInfo.codigoProduto ||
        p.sku === balancaInfo.codigoProdutoAlt ||
        p.codigo_balanca === balancaInfo.codigoProduto ||
        p.codigo_balanca === balancaInfo.codigoProdutoAlt
      );
      
      if (product) {
        quantidade = balancaInfo.peso; // Usa o peso do código de barras
      }
    }
    
    // Se não encontrou como código de balança, busca exata primeiro
    if (!product) {
      product = products.find(p => 
        p.barcode === codigo || 
        p.sku === codigo
      );
    }
    
    // Se ainda não encontrou e tem só um resultado no dropdown, usa ele
    if (!product && filteredProducts.length === 1) {
      product = filteredProducts[0];
    }
    
    if (product) {
      setCurrentProduct(product);
      setShowProductDropdown(false);
      setFilteredProducts([]);
      
      // Se é código de balança, usa o peso do código
      if (balancaInfo.isBalanca) {
        setQuantity(quantidade.toString());
        setBarcodeSearch('');
        // Adiciona direto ao carrinho em vez de chamar addProductToCart
        const discount = parseFloat(itemDiscount) || 0;
        const existingItem = cart.find(item => item.product_id === product.id);
        const qtdTotal = existingItem ? existingItem.quantity + quantidade : quantidade;
        const basePrice = getPrecoAtual(product, qtdTotal);
        const unitPrice = basePrice - discount;
        
        if (existingItem) {
          setCart(cart.map(item => 
            item.product_id === product.id
              ? { ...item, quantity: item.quantity + quantidade, total: (item.quantity + quantidade) * unitPrice, unit_price: unitPrice }
              : item
          ));
        } else {
          setCart([...cart, {
            product_id: product.id,
            product_name: product.name,
            quantity: quantidade,
            unit_price: unitPrice,
            total: quantidade * unitPrice,
            unidade: product.unidade || 'UN',
            codigo_balanca: product.codigo_balanca
          }]);
        }
        setCurrentProduct(null);
        setItemDiscount('');
        
        setTimeout(() => {
          if (barcodeInputRef.current) {
            barcodeInputRef.current.focus();
          }
        }, 100);
      } else {
        // Produto vendido por peso: precisa da balança
        if (product.unidade === 'KG') {
          // 🎯 Lê peso DIRETO do serviço (fonte da verdade)
          const pesoNow = Number(balancaService?.lastWeight) || pesoAtual || 0;
          if (pesoNow > 0) {
            setQuantity(pesoNow.toFixed(3));
            toast.success(`Peso da balança: ${pesoNow.toFixed(3)} kg`);
          } else if (balancaConnected) {
            setQuantity('0');
            toast.warning('Coloque o produto na balança — o peso será lido automaticamente', {
              duration: 4000,
            });
          } else {
            setQuantity('0');
            toast.error(
              'Produto vendido por KG mas a balança não está configurada. Vá em Configurações → Balança para configurar, ou digite o peso manualmente.',
              { duration: 6000 }
            );
          }
        } else {
          setQuantity('1');
        }
        setBarcodeSearch('');
        
        setTimeout(() => {
          if (quantityInputRef.current) {
            quantityInputRef.current.focus();
            quantityInputRef.current.select();
          }
        }, 100);
      }
    } else if (filteredProducts.length > 1) {
      // Tem múltiplos resultados - mantém dropdown aberto
      toast.info('Selecione um produto da lista');
    } else {
      toast.warning('Produto não encontrado');
      setCurrentProduct(null);
      if (barcodeInputRef.current) {
        barcodeInputRef.current.select();
      }
    }
  }, [barcodeSearch, products, filteredProducts, balancaConnected, pesoAtual, cart, itemDiscount, getPrecoAtual]);

  // Navegação no dropdown com teclado
  const handleSearchKeyDown = useCallback((e) => {
    if (!showProductDropdown || filteredProducts.length === 0) {
      if (e.key === 'Enter') {
        e.preventDefault();
        searchByBarcode();
      }
      return;
    }
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedDropdownIndex(prev => 
          prev < filteredProducts.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedDropdownIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredProducts[selectedDropdownIndex]) {
          selectProductFromDropdown(filteredProducts[selectedDropdownIndex]);
        }
        break;
      case 'Escape':
        setShowProductDropdown(false);
        break;
      default:
        break;
    }
  }, [showProductDropdown, filteredProducts, selectedDropdownIndex, selectProductFromDropdown, searchByBarcode]);

  // Função auxiliar para adicionar produto ao carrinho
  const addProductToCart = useCallback((product, qty) => {
    const discount = parseFloat(itemDiscount) || 0;
    const existingItem = cart.find(item => item.product_id === product.id);
    
    // Usar preço baseado no modo e quantidade
    const qtdTotal = existingItem ? existingItem.quantity + qty : qty;
    const basePrice = getPrecoAtual(product, qtdTotal);
    const unitPrice = basePrice - discount;
    
    // Determinar qual tipo de preço está sendo usado
    let tipoPreco = 'varejo';
    const qtdMinAtacado = product.qtd_minima_atacado || 6;
    
    if (modoPreco === 'noturno' && product.preco_noturno) {
      tipoPreco = 'noturno';
    } else if ((modoPreco === 'atacado' || qtdTotal >= qtdMinAtacado) && product.preco_atacado) {
      tipoPreco = 'atacado';
    }
    
    if (existingItem) {
      setCart(cart.map(item =>
        item.product_id === product.id
          ? { 
              ...item, 
              quantity: qtdTotal, 
              unit_price: unitPrice,
              total: qtdTotal * unitPrice,
              tipo_preco: tipoPreco
            }
          : item
      ));
    } else {
      setCart([...cart, {
        product_id: product.id,
        product_name: product.name,
        product_image: product.image || null,
        quantity: qty,
        unit_price: unitPrice,
        total: qty * unitPrice,
        tipo_preco: tipoPreco
      }]);
    }
    
    const precoMsg = tipoPreco !== 'varejo' ? ` (${tipoPreco})` : '';
    toast.success(`${product.name} - ${qty}x ${formatCurrency(unitPrice)}${precoMsg}`);
  }, [cart, itemDiscount, modoPreco, getPrecoAtual]);

  // Handler para Enter no campo de quantidade - adiciona e volta pro código
  const handleQuantityEnter = useCallback(() => {
    if (!currentProduct) return;
    
    const qty = parseFloat(quantity) || 0;
    
    if (qty <= 0) {
      // Produto por KG sem peso: mensagem específica
      if (currentProduct.unidade === 'KG') {
        toast.error(
          balancaConnected
            ? 'Coloque o produto na balança — o peso ainda está zerado.'
            : 'Produto por KG requer balança configurada ou peso digitado manualmente.',
          { duration: 4000 }
        );
      } else {
        toast.error('Quantidade inválida');
      }
      return;
    }
    
    // Verifica estoque apenas se NÃO permitir venda negativa
    if (!pdvConfig.permitirVendaNegativa && qty > currentProduct.stock && currentProduct.tipo_venda === 'unidade') {
      toast.error('Estoque insuficiente');
      return;
    }
    
    addProductToCart(currentProduct, qty);
    
    // Limpa e volta foco para código de barras
    setCurrentProduct(null);
    setQuantity('1');
    setItemDiscount('0');
    
    setTimeout(() => {
      if (barcodeInputRef.current) {
        barcodeInputRef.current.focus();
      }
    }, 100);
  }, [currentProduct, quantity, addProductToCart, pdvConfig.permitirVendaNegativa, balancaConnected]);

  const addToCart = () => {
    if (!currentProduct) {
      toast.error('Selecione um produto primeiro');
      return;
    }
    
    const qty = parseFloat(quantity) || 0;
    const discount = parseFloat(itemDiscount) || 0;

    // Produto por KG exige peso válido (>0)
    if (currentProduct.unidade === 'KG' && qty <= 0) {
      toast.error(
        balancaConnected
          ? 'Coloque o produto na balança — o peso está zerado.'
          : 'Produto por KG requer balança configurada ou peso digitado manualmente.',
        { duration: 4000 }
      );
      return;
    }
    if (qty <= 0) {
      toast.error('Quantidade inválida');
      return;
    }
    
    // Verifica estoque apenas se NÃO permitir venda negativa
    if (!pdvConfig.permitirVendaNegativa && qty > currentProduct.stock && currentProduct.tipo_venda === 'unidade') {
      toast.error('Estoque insuficiente');
      return;
    }

    const existingItem = cart.find(item => item.product_id === currentProduct.id);
    
    // Usar preço baseado no modo e quantidade
    const basePrice = getPrecoAtual(currentProduct, existingItem ? existingItem.quantity + qty : qty);
    const unitPrice = basePrice - discount;
    
    // Determinar qual tipo de preço está sendo usado
    let tipoPreco = 'varejo';
    const qtdMinAtacado = currentProduct.qtd_minima_atacado || 6;
    const qtdTotal = existingItem ? existingItem.quantity + qty : qty;
    
    if (modoPreco === 'noturno' && currentProduct.preco_noturno) {
      tipoPreco = 'noturno';
    } else if ((modoPreco === 'atacado' || qtdTotal >= qtdMinAtacado) && currentProduct.preco_atacado) {
      tipoPreco = 'atacado';
    }
    
    if (existingItem) {
      // Verifica estoque apenas se NÃO permitir venda negativa
      if (!pdvConfig.permitirVendaNegativa && existingItem.quantity + qty > currentProduct.stock && currentProduct.tipo_venda === 'unidade') {
        toast.error('Estoque insuficiente');
        return;
      }
      setCart(cart.map(item =>
        item.product_id === currentProduct.id
          ? { 
              ...item, 
              quantity: item.quantity + qty, 
              unit_price: unitPrice,
              total: (item.quantity + qty) * unitPrice,
              tipo_preco: tipoPreco
            }
          : item
      ));
    } else {
      setCart([...cart, {
        product_id: currentProduct.id,
        product_name: currentProduct.name,
        quantity: qty,
        unit_price: unitPrice,
        total: qty * unitPrice,
        tipo_preco: tipoPreco
      }]);
    }
    
    const precoMsg = tipoPreco !== 'varejo' ? ` (${tipoPreco})` : '';
    toast.success(`${currentProduct.name} adicionado${precoMsg}`);
    setCurrentProduct(null);
    setBarcodeSearch('');
    setQuantity('1');
    setItemDiscount('0');
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
  };

  const subtotal = cart.reduce((sum, item) => sum + item.total, 0);
  const total = subtotal - globalDiscount;

  const handlePayment = () => {
    if (cart.length === 0) {
      toast.error('Carrinho vazio');
      return;
    }
    setPaymentMethods([{ method: 'dinheiro', amount: total }]);
    setShowPaymentModal(true);
  };

  // Função para gerar PIX via Mercado Pago
  const generatePix = async () => {
    setPixLoading(true);
    try {
      const response = await pixAPI.criar(total, `Venda PDV - ${cart.length} item(s)`);
      setPixData(response.data);
      setShowPaymentModal(false);
      setShowPixModal(true);
      toast.success('QR Code PIX gerado!');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao gerar PIX'));
    } finally {
      setPixLoading(false);
    }
  };

  // Função para verificar status do PIX
  const checkPixStatus = async () => {
    if (!pixData?.payment_id) return;
    
    setPixChecking(true);
    try {
      const response = await pixAPI.verificarStatus(pixData.payment_id);
      
      if (response.data.pago) {
        toast.success('Pagamento PIX confirmado!');
        setShowPixModal(false);
        // Processar a venda com PIX já pago
        await completeSaleWithPix();
      } else {
        toast.info(`Status: ${response.data.status_detail || response.data.status}`);
      }
    } catch (error) {
      toast.error('Erro ao verificar pagamento');
    } finally {
      setPixChecking(false);
    }
  };

  // Polling automático para verificar PIX
  useEffect(() => {
    let interval;
    if (showPixModal && pixData?.payment_id) {
      interval = setInterval(async () => {
        try {
          const response = await pixAPI.verificarStatus(pixData.payment_id);
          if (response.data.pago) {
            toast.success('Pagamento PIX confirmado!');
            clearInterval(interval);
            setShowPixModal(false);
            await completeSaleWithPix();
          }
        } catch (error) {
          console.log('Erro ao verificar PIX:', error);
        }
      }, 5000); // Verificar a cada 5 segundos
    }
    return () => clearInterval(interval);
  }, [showPixModal, pixData]);

  // Função para completar venda após PIX confirmado
  const completeSaleWithPix = async () => {
    try {
      // Calcular impostos
      let valorTributos = total * 0.1245;
      let percentualTributos = 12.45;
      
      try {
        const taxResponse = await fetch(`${BACKEND_URL}/api/fiscal/calcular-impostos-venda`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            itens: cart.map(item => ({
              valor_total: item.total,
              ncm: item.ncm || '00000000'
            }))
          })
        });
        
        if (taxResponse.ok) {
          const taxData = await taxResponse.json();
          valorTributos = taxData.totais?.valor_tributos_total || valorTributos;
          percentualTributos = taxData.totais?.percentual_tributos || percentualTributos;
        }
      } catch (taxError) {
        console.warn('Erro ao calcular impostos:', taxError);
      }

      const saleData = {
        items: cart,
        customer_id: selectedCustomer,
        discount: globalDiscount,
        payment_methods: [{ method: 'pix', amount: total }],
        state: selectedState,
        emit_nota: emitirNota,
        tipo_documento: tipoDocumento,
        pix_payment_id: pixData?.payment_id
      };

      const response = await salesAPI.create(saleData);
      
      // Verificar se NFC-e foi emitida com sucesso
      const saleResult = response.data;
      
      setLastSale({
        ...saleResult,
        valor_tributos: valorTributos,
        percentual_tributos: percentualTributos
      });
      
      setCart([]);
      setGlobalDiscount(0);
      setSelectedCustomer(null);
      setEmitirNFCe(true);
      setPaymentMethods([{ method: 'dinheiro', amount: 0 }]);
      setPixData(null);
      setShowReceiptModal(true);
      
      // Mostrar mensagem apropriada baseada no status da NFC-e
      if (saleResult.nfce_key && !saleResult.nfce_simulado) {
        toast.success('Venda realizada! NFC-e autorizada.');
      } else if (saleResult.nfce_simulado) {
        toast.warning('Venda realizada! NFC-e em modo simulação.');
      } else if (saleResult.nfce_erro) {
        toast.error(`Venda salva, mas NFC-e falhou: ${saleResult.nfce_erro}. Você pode reenviar em Vendas.`);
      } else {
        toast.success('Venda realizada com PIX!');
      }
      
      loadProducts();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao finalizar venda'));
    }
  };

  const completeSale = async () => {
    if (completingSale) return;
    const totalPaid = paymentMethods.reduce((sum, pm) => sum + parseFloat(pm.amount || 0), 0);
    
    if (Math.abs(totalPaid - total) > 0.01) {
      toast.error(`Valor pago (${formatCurrency(totalPaid)}) não corresponde ao total (${formatCurrency(total)})`);
      return;
    }

    // Fiado (A Receber) exige cliente cadastrado — a venda gera automaticamente
    // uma conta a receber vinculada ao cliente.
    const hasFiado = paymentMethods.some(pm => pm.method === 'a_receber');
    if (hasFiado && !selectedCustomer) {
      toast.error('Vendas "A Receber" (Fiado) exigem selecionar um cliente. Clique em "Cliente" no topo da tela.');
      return;
    }

    // Se for PIX e Mercado Pago está habilitado, gerar QR Code
    const hasPix = paymentMethods.some(pm => pm.method === 'pix');

    setCompletingSale(true);
    try {
      if (hasPix && paymentSettings?.mercadopago_habilitado) {
        await generatePix();
        return;
      }

      // Se OFFLINE: salvar localmente
      if (!isOnline) {
        const saleData = {
          items: cart,
          customer_id: selectedCustomer,
          discount: globalDiscount,
          payment_methods: paymentMethods.map(pm => ({
            method: pm.method,
            amount: parseFloat(pm.amount)
          })),
          state: selectedState,
          emit_nfce: false, // Offline não pode emitir NFC-e
          total: total,
          subtotal: subtotal,
          created_offline: true,
          offline_timestamp: new Date().toISOString()
        };
        
        const offlineResult = await saveOfflineSale(saleData);
        
        if (offlineResult?.success) {
          setLastSale({
            ...saleData,
            id: `offline-${offlineResult.local_id}`,
            offline: true,
            valor_tributos: total * 0.1245,
            percentual_tributos: 12.45
          });
          
          setCart([]);
          setGlobalDiscount(0);
          setSelectedCustomer(null);
          setEmitirNFCe(true);
          setPaymentMethods([{ method: 'dinheiro', amount: 0 }]);
          setShowPaymentModal(false);
          setShowReceiptModal(true);
          
          toast.warning('Venda salva OFFLINE! Será sincronizada quando houver conexão.', { duration: 5000 });
          return;
        }
      }

      // ONLINE: Calcular impostos antes de criar a venda
      let valorTributos = 0;
      let percentualTributos = 12.45; // Padrão IBS 8.8% + CBS 3.65%
      
      try {
        const taxResponse = await fetch(`${BACKEND_URL}/api/fiscal/calcular-impostos-venda`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            itens: cart.map(item => ({
              valor_total: item.total,
              ncm: item.ncm || '00000000'
            }))
          })
        });
        
        if (taxResponse.ok) {
          const taxData = await taxResponse.json();
          valorTributos = taxData.totais?.valor_tributos_total || 0;
          percentualTributos = taxData.totais?.percentual_tributos || 12.45;
        }
      } catch (taxError) {
        // Se falhar cálculo de impostos, usar padrão
        valorTributos = total * 0.1245;
        console.warn('Erro ao calcular impostos:', taxError);
      }

      const saleData = {
        items: cart,
        customer_id: selectedCustomer,
        discount: globalDiscount,
        payment_methods: paymentMethods.map(pm => ({
          method: pm.method,
          amount: parseFloat(pm.amount)
        })),
        state: selectedState,
        emit_nota: emitirNota,
        tipo_documento: tipoDocumento,
        idempotency_key: (saleIdempotencyKeyRef.current ||= (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`))
      };

      const response = await salesAPI.create(saleData);
      const saleResult = response.data;
      // Venda criada com sucesso: libera nova chave para a próxima venda
      saleIdempotencyKeyRef.current = null;
      
      // Normalizar nfce_erro: pode vir como string, objeto ou array dependendo do erro SEFAZ
      const nfceErroTxt = (() => {
        const e = saleResult.nfce_erro;
        if (!e) return '';
        if (typeof e === 'string') return e;
        if (Array.isArray(e)) return e.map((x) => (typeof x === 'string' ? x : (x?.msg || JSON.stringify(x)))).join('; ');
        if (typeof e === 'object') return e.msg || e.message || JSON.stringify(e);
        return String(e);
      })();

      // Verificar se houve erro de rejeição fiscal (não simulação)
      const erroRejeicao = nfceErroTxt &&
                          nfceErroTxt.includes('Rejeição') &&
                          emitirNota;
      
      if (erroRejeicao) {
        // Se houve rejeição fiscal, mostrar erro mas NÃO limpar carrinho
        // A venda foi salva no banco mas precisa de correção fiscal
        setLastSale({
          ...saleResult,
          valor_tributos: valorTributos,
          percentual_tributos: percentualTributos
        });
        
        const tipoNota = tipoDocumento === 'nfe' ? 'NF-e' : 'NFC-e';
        toast.error(`${tipoNota} rejeitada: ${nfceErroTxt}. Venda salva - acesse Vendas para reenviar.`, {
          duration: 10000
        });
        
        // Fechar modal de pagamento mas NÃO limpar carrinho para permitir ajustes
        setShowPaymentModal(false);
        // NÃO mostrar recibo pois a nota foi rejeitada
        return;
      }
      
      // Adicionar informações de tributos ao lastSale
      setLastSale({
        ...saleResult,
        valor_tributos: valorTributos,
        percentual_tributos: percentualTributos
      });
      
      setCart([]);
      setGlobalDiscount(0);
      setSelectedCustomer(null);
      setEmitirNota(true);
      setTipoDocumento('nfce');
      setPaymentMethods([{ method: 'dinheiro', amount: 0 }]);
      setShowPaymentModal(false);
      setShowReceiptModal(true);
      
      // Mostrar mensagem apropriada baseada no status da nota fiscal
      if (emitirNota) {
        const tipoNota = tipoDocumento === 'nfe' ? 'NF-e' : 'NFC-e';
        if (saleResult.nfce_key && !saleResult.nfce_simulado) {
          toast.success(`Venda realizada! ${tipoNota} autorizada pelo SEFAZ.`);
        } else if (saleResult.nfce_simulado) {
          toast.warning(`Venda realizada! ${tipoNota} em modo simulação (certificado não configurado).`);
        } else if (saleResult.nfce_erro) {
          toast.error(`Venda salva, mas ${tipoNota} falhou: ${nfceErroTxt}. Acesse Vendas para reenviar.`, {
            duration: 8000
          });
        } else {
          toast.info(`Venda realizada! ${tipoNota} não foi emitida.`);
        }
      } else {
        toast.success('Venda realizada sem emissão de NFC-e!');
      }
      loadProducts();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao finalizar venda'));
    } finally {
      setCompletingSale(false);
    }
  };

  const applyGlobalDiscount = () => {
    const discount = parseFloat(discountInput) || 0;
    if (discount > subtotal) {
      toast.error('Desconto não pode ser maior que o subtotal');
      return;
    }
    setGlobalDiscount(discount);
    setShowDiscountModal(false);
    setDiscountInput('');
    toast.success(`Desconto de ${formatCurrency(discount)} aplicado`);
  };

  const selectCustomer = (customer) => {
    setSelectedCustomer(customer.id);
    setShowCustomerModal(false);
    toast.success(`Cliente: ${customer.name}`);
  };

  const filteredCustomers = customers.filter(c => 
    c.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
    (c.cpf_cnpj && c.cpf_cnpj.includes(customerSearch))
  );

  const addPaymentMethod = () => {
    setPaymentMethods([...paymentMethods, { method: 'dinheiro', amount: 0 }]);
  };

  const updatePaymentMethod = (index, field, value) => {
    const updated = [...paymentMethods];
    updated[index][field] = value;
    setPaymentMethods(updated);
  };

  const removePaymentMethod = (index) => {
    if (paymentMethods.length > 1) {
      setPaymentMethods(paymentMethods.filter((_, i) => i !== index));
    }
  };

  const paymentOptionsFallback = [
    { value: 'dinheiro', label: 'Dinheiro' },
    { value: 'cartao_debito', label: 'Débito' },
    { value: 'cartao_credito', label: 'Crédito' },
    { value: 'pix', label: 'PIX' },
    { value: 'a_receber', label: 'A Receber (Fiado)' },
  ];
  const paymentOptions = paymentMethodOptions.length ? paymentMethodOptions : paymentOptionsFallback;
  const labelDaForma = (code) =>
    paymentOptions.find((o) => o.value === code)?.label || code;

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-100 to-slate-200 overflow-hidden" data-testid="pos-page">
      {/* Top Bar - Responsivo */}
      <div className="bg-white shadow-sm px-3 sm:px-6 py-2 sm:py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2 sm:gap-4">
          <div className="text-lg sm:text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <span className="text-amber-500">Fiscal</span>Flow
          </div>
          
          {/* Indicador de Caixa */}
          <div className="hidden sm:flex items-center gap-2 ml-2 sm:ml-4">
            {caixaLoading ? (
              <div className="flex items-center gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-slate-100 rounded-lg">
                <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 animate-spin text-slate-400" />
                <span className="text-xs sm:text-sm text-slate-500">Carregando...</span>
              </div>
            ) : caixaAtual ? (
              <button
                onClick={handleFecharCaixa}
                className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-emerald-100 hover:bg-emerald-200 rounded-lg transition-colors"
                title="Clique para fechar o caixa (F12)"
                data-testid="caixa-status-aberto"
              >
                <Wallet className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-600" />
                <span className="text-xs sm:text-sm font-medium text-emerald-700">Caixa Aberto</span>
                <span className="hidden md:inline text-xs text-emerald-600">
                  ({caixaAtual.vendas_count || 0} vendas)
                </span>
              </button>
            ) : (
              <button
                onClick={handleAbrirCaixa}
                className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors"
                title="Clique para abrir o caixa (F12)"
                data-testid="caixa-status-fechado"
              >
                <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-700">Caixa Fechado</span>
              </button>
            )}
          </div>
          
          {/* Seletor de Modo de Preço */}
          <div className="flex items-center gap-1 ml-4 bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setModoPreco('varejo')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                modoPreco === 'varejo' 
                  ? 'bg-white text-slate-900 shadow-sm' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="modo-varejo"
            >
              Varejo
            </button>
            <button
              onClick={() => setModoPreco('atacado')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                modoPreco === 'atacado' 
                  ? 'bg-blue-500 text-white shadow-sm' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="modo-atacado"
            >
              Atacado
            </button>
            <button
              onClick={() => setModoPreco('noturno')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                modoPreco === 'noturno' 
                  ? 'bg-purple-500 text-white shadow-sm' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="modo-noturno"
            >
              Noturno
            </button>
          </div>
          
          {/* Botões de Sangria/Suprimento (só se caixa aberto) */}
          {caixaAtual && (
            <div className="flex items-center gap-1 ml-2">
              <button
                onClick={() => setShowSangriaModal(true)}
                className="flex items-center gap-1 px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 rounded-md text-sm font-medium transition-colors"
                title="Sangria (retirar dinheiro)"
                data-testid="btn-sangria-header"
              >
                <ArrowDownCircle className="w-4 h-4" />
                Sangria
              </button>
              <button
                onClick={() => setShowSangriaModal(true)}
                className="flex items-center gap-1 px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-md text-sm font-medium transition-colors"
                title="Suprimento (adicionar dinheiro)"
                data-testid="btn-suprimento-header"
              >
                <ArrowUpCircle className="w-4 h-4" />
                Suprimento
              </button>
            </div>
          )}
        </div>
        
        {/* Indicador Online/Offline */}
        <div className="ml-2">
          <OfflineIndicator 
            isOnline={isOnline}
            syncStatus={syncStatus}
            onSync={syncNow}
            variant="compact"
          />
        </div>
        
        {/* Display Bar */}
        <div className="flex-1 max-w-2xl mx-8">
          <div className="bg-slate-50 border-2 border-slate-200 rounded-2xl px-6 py-4 text-right">
            <span className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {formatCurrency(total)}
            </span>
          </div>
        </div>
        
        {/* Quick Actions */}
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="icon" 
            className="h-12 w-12 border-slate-300 hover:bg-slate-50"
            onClick={() => navigate('/dashboard')}
            title="Voltar ao Dashboard"
          >
            <Home className="h-5 w-5 text-slate-600" />
          </Button>
          <Button variant="outline" size="icon" className="h-12 w-12 border-amber-400 hover:bg-amber-50">
            <Monitor className="h-5 w-5 text-amber-600" />
          </Button>
          <Button variant="outline" size="icon" className="h-12 w-12 border-amber-400 hover:bg-amber-50" onClick={() => setShowCustomerModal(true)}>
            <Users className="h-5 w-5 text-amber-600" />
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex p-4 gap-4 overflow-hidden">
        {/* Left Panel - Product Entry */}
        <div className="w-96 flex flex-col gap-4">
          {/* Barcode Search with Dropdown */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <label className="text-sm font-medium text-slate-600 mb-2 block">
              Pesquisar Produto
              <span className="text-xs text-slate-400 ml-2">(código ou nome)</span>
            </label>
            <div className="relative">
              <div className="flex gap-2">
                <Input
                  ref={barcodeInputRef}
                  placeholder="Digite código ou nome..."
                  value={barcodeSearch}
                  onChange={(e) => {
                    setBarcodeSearch(e.target.value);
                    filterProducts(e.target.value);
                  }}
                  onKeyDown={handleSearchKeyDown}
                  onFocus={() => {
                    if (barcodeSearch.length >= 2) {
                      filterProducts(barcodeSearch);
                    }
                  }}
                  onBlur={() => {
                    // Delay para permitir clique no dropdown
                    setTimeout(() => setShowProductDropdown(false), 200);
                  }}
                  className="flex-1 h-12 text-lg border-slate-300"
                  data-testid="barcode-input"
                  autoFocus
                />
                <Button 
                  onClick={searchByBarcode}
                  className="h-12 w-12 bg-amber-500 hover:bg-amber-600 text-white"
                  data-testid="search-barcode-btn"
                >
                  <Search className="h-5 w-5" />
                </Button>
              </div>
              
              {/* Dropdown de produtos */}
              {showProductDropdown && filteredProducts.length > 0 && (
                <div 
                  ref={dropdownRef}
                  className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto"
                >
                  {filteredProducts.map((product, index) => (
                    <div
                      key={product.id}
                      onClick={() => selectProductFromDropdown(product)}
                      className={`px-4 py-3 cursor-pointer border-b border-slate-100 last:border-0 flex items-center justify-between ${
                        index === selectedDropdownIndex ? 'bg-amber-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex-1">
                        <p className="font-medium text-slate-800">{product.name}</p>
                        <p className="text-xs text-slate-500">
                          {product.barcode && `Cód: ${product.barcode}`}
                          {product.barcode && product.sku && ' | '}
                          {product.sku && `SKU: ${product.sku}`}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-amber-600">{formatCurrency(product.price)}</p>
                        <p className="text-xs text-slate-500">
                          {product.unidade === 'KG' ? 'por kg' : 'unid'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Balança */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-600">Balança</label>
              {balancaConnected ? (
                <span className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${
                  balancaError 
                    ? 'bg-rose-100 text-rose-700' 
                    : 'bg-emerald-100 text-emerald-700'
                }`}>
                  <span className={`w-2 h-2 rounded-full ${
                    balancaError ? 'bg-rose-500' : 'bg-emerald-500 animate-pulse'
                  }`}></span>
                  {balancaError ? 'Erro' : balancaName}
                </span>
              ) : balancaConfig?.habilitada ? (
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
                  Aguardando...
                </span>
              ) : (
                <span className="text-xs bg-slate-100 text-slate-500 px-2 py-1 rounded-full">
                  Desabilitada
                </span>
              )}
            </div>
            
            {balancaConnected ? (
              <div className="flex items-center gap-2">
                <div className="flex-1 h-14 bg-slate-900 rounded-lg flex items-center justify-center">
                  <span className="text-3xl font-mono font-bold text-emerald-400">
                    {pesoAtual.toFixed(3)} kg
                  </span>
                </div>
                <Button 
                  onClick={desconectarBalanca}
                  variant="outline"
                  className="h-14 px-3 text-rose-600 border-rose-300 hover:bg-rose-50"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            ) : balancaConfig?.habilitada ? (
              <Button 
                onClick={conectarBalanca}
                variant="outline"
                className="w-full h-12 border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                <Scale className="h-5 w-5 mr-2" />
                Reconectar Balança
              </Button>
            ) : (
              <div className="text-center py-3 text-slate-400 text-sm">
                Configure em Configurações → Balança
              </div>
            )}
          </div>

          {/* Product Fields */}
          <div className="bg-white rounded-xl shadow-sm p-4 flex-1 flex flex-col gap-3">
            {/* Quantity / Peso */}
            <div className="flex items-center">
              <div className="w-24 bg-amber-500 text-white py-3 px-4 rounded-l-lg font-semibold text-sm">
                {currentProduct?.unidade === 'KG' ? 'Peso' : 'Quant.'}
              </div>
              <Input
                ref={quantityInputRef}
                type="number"
                min="0.001"
                step={currentProduct?.unidade === 'KG' ? '0.001' : '1'}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleQuantityEnter()}
                className={`flex-1 h-12 rounded-l-none border-l-0 text-lg font-mono ${
                  currentProduct?.unidade === 'KG' && !canEditWeight() 
                    ? 'bg-slate-100 text-emerald-600 cursor-not-allowed' 
                    : ''
                }`}
                data-testid="quantity-input"
                readOnly={currentProduct?.unidade === 'KG' && !canEditWeight()}
                title={currentProduct?.unidade === 'KG' && !canEditWeight() 
                  ? 'Peso da balança (automático)' 
                  : ''
                }
              />
              {currentProduct?.unidade === 'KG' && (
                <span className="px-3 text-sm text-slate-500 flex items-center gap-1">
                  kg
                  {!canEditWeight() && (
                    <Scale className="w-4 h-4 text-emerald-500" />
                  )}
                </span>
              )}
            </div>
            
            {/* Aviso sobre input de peso */}
            {currentProduct?.unidade === 'KG' && !canEditWeight() && (
              <p className="text-xs text-emerald-600 -mt-1 ml-24">
                Peso da balança • Para digitar manualmente, desconecte a balança
              </p>
            )}
            {currentProduct?.unidade === 'KG' && canEditWeight() && balancaConfig?.habilitada && (
              <p className="text-xs text-amber-600 -mt-1 ml-24">
                Balança desconectada • Digite o peso manualmente
              </p>
            )}

            {/* Discount */}
            <div className="flex items-center">
              <div className="w-24 bg-amber-500 text-white py-3 px-4 rounded-l-lg font-semibold text-sm">
                Desc.
              </div>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={itemDiscount}
                onChange={(e) => setItemDiscount(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleQuantityEnter()}
                className="flex-1 h-12 rounded-l-none border-l-0 text-lg"
                placeholder="0,00"
                data-testid="item-discount-input"
              />
            </div>

            {/* Unit Value */}
            <div className="flex items-center">
              <div className="w-24 bg-amber-500 text-white py-3 px-4 rounded-l-lg font-semibold text-sm">
                Vlr.Unid.
              </div>
              <div className="flex-1 h-12 bg-slate-50 border border-slate-300 rounded-r-lg flex items-center px-4 text-lg font-medium">
                {currentProduct ? formatCurrency(getPrecoAtual(currentProduct, parseFloat(quantity) || 1)) : 'R$ 0,00'}
              </div>
            </div>

            {/* Total Value */}
            <div className="flex items-center">
              <div className="w-24 bg-amber-500 text-white py-3 px-4 rounded-l-lg font-semibold text-sm">
                Vlr.Total
              </div>
              <div className="flex-1 h-12 bg-slate-50 border border-slate-300 rounded-r-lg flex items-center px-4 text-lg font-bold">
                {currentProduct ? formatCurrency((getPrecoAtual(currentProduct, parseFloat(quantity) || 1) - (parseFloat(itemDiscount) || 0)) * (parseFloat(quantity) || 1)) : 'R$ 0,00'}
              </div>
            </div>

            {/* Current Product Info - Indicação visual do fluxo */}
            {currentProduct && (
              <div className="mt-2 p-3 bg-emerald-50 border-2 border-emerald-400 rounded-lg animate-pulse">
                <p className="text-xs text-emerald-600 font-medium mb-2 text-center">
                  Pressione ENTER para adicionar ao carrinho
                </p>
                <div className="flex gap-3">
                  {currentProduct.image ? (
                    <img 
                      src={currentProduct.image} 
                      alt={currentProduct.name}
                      className="w-16 h-16 object-cover rounded-lg border"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-slate-200 rounded-lg flex items-center justify-center">
                      <ShoppingCart className="w-6 h-6 text-slate-400" />
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="font-semibold text-amber-800">{currentProduct.name}</p>
                    <p className="text-sm text-amber-600">Estoque: {currentProduct.stock}</p>
                    {/* Preços disponíveis */}
                    <div className="flex flex-wrap gap-2 mt-1">
                      <span className={`text-sm px-2 py-0.5 rounded ${modoPreco === 'varejo' && !currentProduct.preco_atacado ? 'bg-emerald-100 text-emerald-700 font-bold' : 'bg-slate-100 text-slate-600'}`}>
                        Varejo: {formatCurrency(currentProduct.price)}
                      </span>
                      {currentProduct.preco_atacado && (
                        <span className={`text-sm px-2 py-0.5 rounded ${modoPreco === 'atacado' ? 'bg-blue-100 text-blue-700 font-bold' : 'bg-slate-100 text-slate-600'}`}>
                          Atacado: {formatCurrency(currentProduct.preco_atacado)}
                          <span className="text-xs ml-1">(≥{currentProduct.qtd_minima_atacado || 6})</span>
                        </span>
                      )}
                      {currentProduct.preco_noturno && (
                        <span className={`text-sm px-2 py-0.5 rounded ${modoPreco === 'noturno' ? 'bg-purple-100 text-purple-700 font-bold' : 'bg-slate-100 text-slate-600'}`}>
                          Noturno: {formatCurrency(currentProduct.preco_noturno)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Add to Cart Button */}
            <Button 
              onClick={addToCart}
              disabled={!currentProduct}
              className="mt-auto h-14 bg-emerald-600 hover:bg-emerald-700 text-white text-lg font-semibold"
              data-testid="add-to-cart-btn"
            >
              <ShoppingCart className="h-5 w-5 mr-2" />
              Adicionar ao Carrinho
            </Button>
          </div>
        </div>

        {/* Right Panel - Cart */}
        <div className="flex-1 flex flex-col gap-4">
          {/* Customer & Cart Header */}
          <div className="flex gap-4">
            {/* Customer Search */}
            <div className="flex-1 bg-white rounded-xl shadow-sm p-3 flex items-center gap-3">
              <User className="h-5 w-5 text-slate-400" />
              <Input
                placeholder="Buscar cliente..."
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                onFocus={() => setShowCustomerModal(true)}
                className="flex-1 border-0 focus-visible:ring-0"
              />
              <Button 
                onClick={() => setShowCustomerModal(true)}
                className="h-10 w-10 bg-amber-500 hover:bg-amber-600 text-white"
              >
                <Search className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Cart Icon */}
            <div className="bg-white rounded-xl shadow-sm p-3 flex items-center gap-2 min-w-32">
              <ShoppingCart className="h-5 w-5 text-amber-600" />
              <span className="font-semibold text-slate-700">{cart.length} itens</span>
            </div>
          </div>

          {/* Cart Items */}
          <div className="flex-1 bg-white rounded-xl shadow-sm overflow-hidden flex flex-col">
            <ScrollArea className="flex-1 p-4">
              {cart.length === 0 ? (
                <div className="h-full flex items-center justify-center text-slate-400">
                  <div className="text-center">
                    <ShoppingCart className="h-16 w-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg">Carrinho vazio</p>
                    <p className="text-sm">Escaneie um produto para começar</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {cart.map((item, index) => (
                    <div 
                      key={item.product_id} 
                      className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                      data-testid={`cart-item-${item.product_id}`}
                    >
                      {/* Foto do produto */}
                      {item.product_image ? (
                        <img 
                          src={item.product_image} 
                          alt={item.product_name}
                          className="w-12 h-12 object-cover rounded-lg border"
                        />
                      ) : (
                        <span className="w-12 h-12 bg-amber-500 text-white rounded-lg flex items-center justify-center font-semibold text-sm">
                          {index + 1}
                        </span>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{item.product_name}</p>
                        <p className="text-sm text-slate-500">
                          {item.quantity}x {formatCurrency(item.unit_price)}
                        </p>
                      </div>
                      <span className="font-bold text-lg min-w-24 text-right">
                        {formatCurrency(item.total)}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeFromCart(item.product_id)}
                        className="text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>

            {/* Subtotal Bar */}
            <div className="border-t border-slate-200">
              <div className="flex items-center">
                <div className="bg-amber-500 text-white py-4 px-6 font-bold text-lg">
                  Subtotal
                </div>
                <div className="flex-1 py-4 px-6 text-right">
                  <span className="text-2xl font-bold text-slate-800">{formatCurrency(subtotal)}</span>
                </div>
              </div>
              {globalDiscount > 0 && (
                <div className="flex items-center bg-emerald-50 px-6 py-2">
                  <span className="text-emerald-700 font-medium">Desconto aplicado:</span>
                  <span className="ml-auto text-emerald-700 font-bold">-{formatCurrency(globalDiscount)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Message Area */}
          {selectedCustomer && (
            <div className="bg-white rounded-xl shadow-sm p-3 flex items-center gap-3">
              <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                <User className="h-4 w-4 text-amber-600" />
              </div>
              <span className="text-slate-700">
                Cliente: <strong>{customers.find(c => c.id === selectedCustomer)?.name || 'Selecionado'}</strong>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Toolbar */}
      <div className="bg-white shadow-lg px-6 py-2 flex items-center justify-center gap-2 flex-shrink-0">
        <Button 
          onClick={() => setShowCustomerModal(true)}
          variant="outline" 
          className="h-12 px-3 flex flex-col items-center gap-0.5 border-slate-300 hover:bg-slate-50"
        >
          <User className="h-4 w-4 text-slate-600" />
          <span className="text-xs text-slate-500">F11</span>
        </Button>
        
        <Button 
          variant="outline" 
          className="h-12 px-3 flex flex-col items-center gap-0.5 border-slate-300 hover:bg-slate-50"
        >
          <Settings className="h-4 w-4 text-slate-600" />
          <span className="text-xs text-slate-500">F2</span>
        </Button>
        
        <Button 
          onClick={() => setShowDiscountModal(true)}
          variant="outline" 
          className="h-12 px-3 flex flex-col items-center gap-0.5 border-slate-300 hover:bg-slate-50"
        >
          <Percent className="h-4 w-4 text-slate-600" />
          <span className="text-xs text-slate-500">F7</span>
        </Button>
        
        <Button 
          onClick={handlePayment}
          variant="outline" 
          className="h-12 px-3 flex flex-col items-center gap-0.5 border-slate-300 hover:bg-slate-50"
        >
          <DollarSign className="h-4 w-4 text-slate-600" />
          <span className="text-xs text-slate-500">F8</span>
        </Button>
        
        <Button 
          variant="outline" 
          className="h-12 px-3 flex flex-col items-center gap-0.5 border-slate-300 hover:bg-slate-50"
        >
          <FileText className="h-4 w-4 text-slate-600" />
          <span className="text-xs text-slate-500">F9</span>
        </Button>
        
        <Button 
          onClick={() => {
            if (cart.length > 0 && window.confirm('Cancelar venda atual?')) {
              setCart([]);
              setGlobalDiscount(0);
              setSelectedCustomer(null);
              toast.info('Venda cancelada');
            }
          }}
          variant="outline" 
          className="h-12 px-3 flex flex-col items-center gap-0.5 border-rose-200 hover:bg-rose-50 text-rose-500"
        >
          <X className="h-4 w-4" />
          <span className="text-xs">F10</span>
        </Button>
        
        <Button 
          onClick={handlePayment}
          disabled={cart.length === 0}
          className="h-12 px-5 flex flex-col items-center gap-0.5 bg-amber-500 hover:bg-amber-600 text-white"
          data-testid="finalize-sale-btn"
        >
          <Check className="h-4 w-4" />
          <span className="text-xs">F5</span>
        </Button>
      </div>

      {/* Customer Modal */}
      <Dialog open={showCustomerModal} onOpenChange={setShowCustomerModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Selecionar Cliente</DialogTitle>
            <DialogDescription>Busque e selecione um cliente para a venda</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder="Buscar por nome ou CPF/CNPJ..."
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              autoFocus
            />
            <ScrollArea className="h-64">
              <div className="space-y-2">
                {filteredCustomers.map(customer => (
                  <div
                    key={customer.id}
                    onClick={() => selectCustomer(customer)}
                    className="p-3 rounded-lg hover:bg-slate-100 cursor-pointer border border-slate-200"
                  >
                    <p className="font-medium">{customer.name}</p>
                    {customer.cpf_cnpj && <p className="text-sm text-slate-500">{customer.cpf_cnpj}</p>}
                  </div>
                ))}
                {filteredCustomers.length === 0 && (
                  <p className="text-center text-slate-500 py-4">Nenhum cliente encontrado</p>
                )}
              </div>
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCustomerModal(false)}>Fechar</Button>
            <Button onClick={() => { setSelectedCustomer(null); setShowCustomerModal(false); }}>
              Sem Cliente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discount Modal */}
      <Dialog open={showDiscountModal} onOpenChange={setShowDiscountModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Aplicar Desconto</DialogTitle>
            <DialogDescription>Informe o valor do desconto na venda</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Subtotal atual</Label>
              <p className="text-2xl font-bold">{formatCurrency(subtotal)}</p>
            </div>
            <div>
              <Label>Valor do desconto (R$)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={discountInput}
                onChange={(e) => setDiscountInput(e.target.value)}
                placeholder="0,00"
                className="text-lg"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDiscountModal(false)}>Cancelar</Button>
            <Button onClick={applyGlobalDiscount} className="bg-amber-500 hover:bg-amber-600">
              Aplicar Desconto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="w-[95vw] max-w-lg sm:max-w-xl md:max-w-2xl h-auto max-h-[80vh] flex flex-col p-0" data-testid="payment-modal">
          <DialogHeader className="px-4 pt-4 pb-2 flex-shrink-0 border-b">
            <DialogTitle className="text-lg sm:text-xl">Pagamento</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">Selecione a forma de pagamento</DialogDescription>
          </DialogHeader>
          
          {/* Área scrollável */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            <div className="bg-amber-50 p-2 sm:p-3 rounded-lg border border-amber-200">
              <div className="flex justify-between items-center">
                <span className="font-medium text-sm sm:text-base">Total:</span>
                <span className="text-lg sm:text-xl font-bold text-amber-600">{formatCurrency(total)}</span>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label className="font-semibold text-xs sm:text-sm">Forma de Pagamento</Label>
                <Button size="sm" variant="outline" onClick={addPaymentMethod} className="h-6 sm:h-7 text-xs px-2">
                  + Adicionar
                </Button>
              </div>

              {paymentMethods.map((pm, index) => (
                <div key={index} className="p-2 bg-slate-50 rounded-lg">
                  <div className="flex gap-2 sm:gap-3 flex-wrap mb-2">
                    {paymentOptions.map((option) => (
                      <label key={option.value} className="flex items-center gap-1 cursor-pointer text-xs sm:text-sm">
                        <input
                          type="radio"
                          name={`payment-${index}`}
                          value={option.value}
                          checked={pm.method === option.value}
                          onChange={(e) => updatePaymentMethod(index, 'method', e.target.value)}
                          className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-amber-500"
                        />
                        {option.label}
                      </label>
                    ))}
                  </div>
                  
                  <div className="flex gap-2 items-center">
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Valor"
                      value={pm.amount}
                      onChange={(e) => updatePaymentMethod(index, 'amount', e.target.value)}
                      className="flex-1 h-7 sm:h-8 text-xs sm:text-sm"
                    />
                    {paymentMethods.length > 1 && (
                      <Button size="sm" variant="ghost" onClick={() => removePaymentMethod(index)} className="text-rose-500 h-7 w-7 p-0">
                        <X className="w-3 h-3 sm:w-4 sm:h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}

              <div className="flex justify-between items-center bg-blue-50 px-2 sm:px-3 py-1.5 sm:py-2 rounded border border-blue-200 text-xs sm:text-sm">
                <span>Total Pago:</span>
                <span className="font-bold">
                  {formatCurrency(paymentMethods.reduce((sum, pm) => sum + parseFloat(pm.amount || 0), 0))}
                </span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
              <div className="flex-1 space-y-1">
                <Label className="text-xs sm:text-sm">Estado</Label>
                <div className="flex gap-2 sm:gap-3">
                  {['SP', 'RJ', 'MG'].map(state => (
                    <label key={state} className="flex items-center gap-1 cursor-pointer text-xs sm:text-sm">
                      <input
                        type="radio"
                        name="state"
                        value={state}
                        checked={selectedState === state}
                        onChange={(e) => setSelectedState(e.target.value)}
                        className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-amber-500"
                      />
                      {state}
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex items-center space-x-2 bg-slate-50 p-2 rounded-lg">
                <Checkbox 
                  id="emit-nota" 
                  checked={emitirNota}
                  onCheckedChange={setEmitirNota}
                  className="h-4 w-4"
                />
                <label htmlFor="emit-nota" className="text-xs sm:text-sm font-medium cursor-pointer">
                  Emitir Nota Fiscal
                </label>
              </div>
            </div>
            
            {emitirNota && (
              <div className="bg-blue-50 p-2 sm:p-3 rounded-lg border border-blue-200">
                <label className="text-xs sm:text-sm font-medium text-blue-800 mb-2 block">Tipo de Documento</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setTipoDocumento('nfce')}
                    className={`p-2 rounded-lg border-2 transition-all text-left ${
                      tipoDocumento === 'nfce' 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <span className="font-semibold text-xs sm:text-sm block">NFC-e</span>
                    <span className="text-[10px] sm:text-xs text-slate-500">Consumidor</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setTipoDocumento('nfe')}
                    className={`p-2 rounded-lg border-2 transition-all text-left ${
                      tipoDocumento === 'nfe' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <span className="font-semibold text-xs sm:text-sm block">NF-e</span>
                    <span className="text-[10px] sm:text-xs text-slate-500">Empresa</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Footer fixo - sempre visível */}
          <div className="flex-shrink-0 border-t bg-white px-4 py-3 flex flex-col sm:flex-row gap-2 sm:justify-end">
            <Button variant="outline" onClick={() => setShowPaymentModal(false)} className="h-9 sm:h-10 w-full sm:w-auto order-2 sm:order-1">
              Cancelar
            </Button>
            <Button onClick={completeSale} disabled={completingSale} data-testid="finalizar-venda-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 sm:h-10 w-full sm:w-auto order-1 sm:order-2 disabled:opacity-70">
              {completingSale ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processando...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  {emitirNota ? `Finalizar ${tipoDocumento === 'nfe' ? 'NF-e' : 'NFC-e'}` : 'Finalizar'}
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt Modal - Cupom Fiscal Estilo Supermercado */}
      <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
        <DialogContent className="max-w-sm p-0 bg-white" data-testid="receipt-modal">
          {/* Cupom Fiscal - Estilo Supermercado */}
          <div id="cupom-fiscal" className="bg-white p-4 font-mono text-xs" style={{ fontFamily: 'Courier New, monospace' }}>
            {lastSale && (
              <div className="text-center">
                {/* Cabeçalho */}
                <div className="border-b border-dashed border-slate-400 pb-3 mb-3">
                  <p className="font-bold text-sm">{(companyInfo.razao_social || companyInfo.nome_fantasia || 'EMPRESA').toUpperCase()}</p>
                  {companyInfo.nome_fantasia && companyInfo.razao_social && companyInfo.nome_fantasia !== companyInfo.razao_social && (
                    <p>{companyInfo.nome_fantasia}</p>
                  )}
                  {companyInfo.cnpj && <p>CNPJ: {companyInfo.cnpj}</p>}
                  {(companyInfo.inscricao_estadual || companyInfo.ie) && <p>IE: {companyInfo.inscricao_estadual || companyInfo.ie}</p>}
                  {(companyInfo.endereco || companyInfo.logradouro) && (
                    <p>
                      {companyInfo.endereco || companyInfo.logradouro}
                      {companyInfo.numero ? `, ${companyInfo.numero}` : ''}
                      {companyInfo.bairro ? ` - ${companyInfo.bairro}` : ''}
                    </p>
                  )}
                  {(companyInfo.cidade || companyInfo.uf) && (
                    <p>
                      {companyInfo.cidade || ''}{companyInfo.uf ? ` - ${companyInfo.uf}` : ''}
                      {companyInfo.cep ? ` CEP ${companyInfo.cep}` : ''}
                    </p>
                  )}
                  {companyInfo.telefone && <p>Tel: {companyInfo.telefone}</p>}
                  {!companyInfo.razao_social && !companyInfo.nome_fantasia && (
                    <p className="text-amber-600 text-[10px]">⚠ Cadastre os dados da empresa em Configurações → Empresa</p>
                  )}
                  {lastSale.nfce_key ? (
                    <>
                      <p className="mt-2 font-bold">DOCUMENTO AUXILIAR DA</p>
                      <p className="font-bold">NOTA FISCAL DE CONSUMIDOR</p>
                      <p className="font-bold">ELETRÔNICA</p>
                    </>
                  ) : (
                    <>
                      <p className="mt-2 font-bold">COMPROVANTE NÃO FISCAL</p>
                      <p className="text-[10px] text-slate-500">Sem valor fiscal</p>
                    </>
                  )}
                </div>

                {/* Status NFC-e */}
                {lastSale.nfce_key && !lastSale.nfce_simulado && (
                  <div className="border-b border-dashed border-slate-400 pb-3 mb-3">
                    <p className="font-bold">NFC-e Nº {String(lastSale.nfce_number).padStart(9, '0')}</p>
                    <p>SÉRIE: {lastSale.nfce_series || '001'}</p>
                    <p>{new Date(lastSale.created_at).toLocaleString('pt-BR')}</p>
                  </div>
                )}

                {lastSale.nfce_simulado && (
                  <div className="border-b border-dashed border-slate-400 pb-3 mb-3 bg-amber-50 -mx-4 px-4 py-2">
                    <p className="font-bold text-amber-700">*** SIMULAÇÃO ***</p>
                    <p className="text-amber-600">SEM VALOR FISCAL</p>
                  </div>
                )}

                {/* Itens */}
                <div className="border-b border-dashed border-slate-400 pb-3 mb-3 text-left">
                  <p className="font-bold text-center mb-2">CUPOM FISCAL</p>
                  <p className="text-[10px] mb-1">ITEM CÓDIGO DESCRIÇÃO</p>
                  <p className="text-[10px] mb-2">QTD UN VL UNIT VL TOTAL</p>
                  <div className="border-t border-dotted border-slate-300 pt-1">
                    {lastSale.items?.map((item, i) => (
                      <div key={i} className="mb-1">
                        <p>{String(i + 1).padStart(3, '0')} {item.product_name?.substring(0, 20)}</p>
                        <p className="text-right">
                          {item.quantity} x {formatCurrency(item.unit_price)} = {formatCurrency(item.total)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Totais */}
                <div className="border-b border-dashed border-slate-400 pb-3 mb-3 text-left">
                  {lastSale.discount > 0 && (
                    <p className="flex justify-between">
                      <span>DESCONTO:</span>
                      <span>-{formatCurrency(lastSale.discount)}</span>
                    </p>
                  )}
                  <p className="flex justify-between font-bold text-sm">
                    <span>TOTAL R$</span>
                    <span>{formatCurrency(lastSale.total)}</span>
                  </p>
                </div>

                {/* Forma de Pagamento */}
                <div className="border-b border-dashed border-slate-400 pb-3 mb-3 text-left">
                  <p className="font-bold mb-1">FORMA DE PAGAMENTO</p>
                  {lastSale.payment_methods?.map((pm, i) => (
                    <p key={i} className="flex justify-between">
                      <span>
                        {pm.method === 'dinheiro' ? 'Dinheiro' : 
                         pm.method === 'pix' ? 'PIX' : 
                         pm.method === 'cartao_credito' ? 'Cartão Crédito' : 
                         pm.method === 'cartao_debito' ? 'Cartão Débito' : labelDaForma(pm.method)}
                      </span>
                      <span>{formatCurrency(pm.amount)}</span>
                    </p>
                  ))}
                </div>

                {/* Tributos */}
                {lastSale.valor_tributos > 0 && (
                  <div className="border-b border-dashed border-slate-400 pb-3 mb-3 text-left">
                    <p className="text-[10px]">Val Aprox Tributos R$ {formatCurrency(lastSale.valor_tributos)}</p>
                    <p className="text-[10px]">({lastSale.percentual_tributos?.toFixed(2) || '12.45'}%) Fonte: IBPT</p>
                  </div>
                )}

                {/* Chave de Acesso */}
                {lastSale.nfce_key && (
                  <div className="border-b border-dashed border-slate-400 pb-3 mb-3">
                    <p className="font-bold mb-1">CHAVE DE ACESSO</p>
                    <p className="text-[9px] break-all leading-tight">
                      {lastSale.nfce_key.match(/.{1,4}/g)?.join(' ')}
                    </p>
                  </div>
                )}

                {/* QR Code */}
                {lastSale.nfce_qrcode_url && (
                  <div className="py-3 flex flex-col items-center">
                    <p className="font-bold mb-2">CONSULTE PELA CHAVE DE ACESSO EM</p>
                    <p className="text-[10px] mb-3">www.nfce.fazenda.sp.gov.br</p>
                    <QRCodeSVG 
                      value={lastSale.nfce_qrcode_url} 
                      size={150}
                      level="M"
                      includeMargin={true}
                    />
                    <p className="text-[10px] mt-2">Consumidor: Não identificado</p>
                  </div>
                )}

                {/* Protocolo */}
                {lastSale.nfce_protocol && (
                  <div className="border-t border-dashed border-slate-400 pt-3 mt-3">
                    <p className="text-[10px]">Protocolo de Autorização:</p>
                    <p className="text-[10px] font-bold">{lastSale.nfce_protocol}</p>
                    <p className="text-[10px]">{new Date(lastSale.created_at).toLocaleString('pt-BR')}</p>
                  </div>
                )}

                {/* Rodapé */}
                <div className="border-t border-dashed border-slate-400 pt-3 mt-3">
                  <p className="text-[10px]">Obrigado! Volte sempre!</p>
                  <p className="text-[10px] mt-2">******************************</p>
                </div>
              </div>
            )}
          </div>

          {/* Botões de Ação */}
          <div className="p-4 border-t bg-slate-50 space-y-2">
            <Button 
              onClick={() => {
                const printContent = document.getElementById('cupom-fiscal');
                const printWindow = window.open('', '', 'width=300,height=600');
                printWindow.document.write(`
                  <html>
                    <head>
                      <title>Cupom Fiscal</title>
                      <style>
                        @page { size: 80mm auto; margin: 0; }
                        body { 
                          font-family: 'Courier New', monospace; 
                          font-size: 10px; 
                          padding: 10px;
                          max-width: 80mm;
                          margin: 0 auto;
                        }
                        * { box-sizing: border-box; }
                        .text-center { text-align: center; }
                        .text-left { text-align: left; }
                        .text-right { text-align: right; }
                        .font-bold { font-weight: bold; }
                        .flex { display: flex; }
                        .justify-between { justify-content: space-between; }
                        .border-dashed { border-style: dashed; }
                        .border-dotted { border-style: dotted; }
                        .border-b { border-bottom: 1px dashed #999; }
                        .border-t { border-top: 1px dashed #999; }
                        .pb-3 { padding-bottom: 10px; }
                        .pt-3 { padding-top: 10px; }
                        .mb-1 { margin-bottom: 4px; }
                        .mb-2 { margin-bottom: 8px; }
                        .mb-3 { margin-bottom: 12px; }
                        .mt-2 { margin-top: 8px; }
                        .mt-3 { margin-top: 12px; }
                        .py-3 { padding: 10px 0; }
                        .text-sm { font-size: 12px; }
                        svg { display: block; margin: 0 auto; }
                      </style>
                    </head>
                    <body>
                      ${printContent.innerHTML}
                    </body>
                  </html>
                `);
                printWindow.document.close();
                printWindow.focus();
                setTimeout(() => {
                  printWindow.print();
                  printWindow.close();
                }, 250);
              }} 
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="print-receipt-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Imprimir Cupom
            </Button>
            <Button 
              onClick={() => setShowReceiptModal(false)} 
              variant="outline"
              className="w-full"
              data-testid="new-sale-btn"
            >
              Nova Venda
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal PIX - Aguardando Pagamento */}
      <Dialog open={showPixModal} onOpenChange={setShowPixModal}>
        <DialogContent className="max-w-md" data-testid="pix-modal">
          <DialogHeader>
            <DialogTitle className="text-center text-xl">Pagamento PIX</DialogTitle>
            <DialogDescription className="text-center">
              Escaneie o QR Code ou copie o código PIX
            </DialogDescription>
          </DialogHeader>
          
          {pixData && (
            <div className="space-y-4">
              {/* Valor */}
              <div className="text-center bg-emerald-50 p-4 rounded-lg">
                <p className="text-sm text-slate-600">Valor a pagar</p>
                <p className="text-3xl font-bold text-emerald-600">{formatCurrency(pixData.valor || total)}</p>
              </div>

              {/* QR Code */}
              <div className="flex justify-center p-4 bg-white border rounded-lg">
                {pixData.qr_code ? (
                  <QRCodeSVG 
                    value={pixData.qr_code} 
                    size={200}
                    level="M"
                    includeMargin={true}
                  />
                ) : pixData.qr_code_base64 ? (
                  <img 
                    src={`data:image/png;base64,${pixData.qr_code_base64}`} 
                    alt="QR Code PIX"
                    className="w-48 h-48"
                  />
                ) : (
                  <div className="w-48 h-48 flex items-center justify-center bg-slate-100 rounded">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  </div>
                )}
              </div>

              {/* Código Copia e Cola */}
              {pixData.qr_code && (
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Código PIX (Copia e Cola)</Label>
                  <div className="flex gap-2">
                    <Input 
                      value={pixData.qr_code} 
                      readOnly 
                      className="text-xs font-mono"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        navigator.clipboard.writeText(pixData.qr_code);
                        toast.success('Código copiado!');
                      }}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Status */}
              <div className="text-center p-3 bg-amber-50 rounded-lg">
                <div className="flex items-center justify-center gap-2 text-amber-700">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm font-medium">Aguardando pagamento...</span>
                </div>
                <p className="text-xs text-amber-600 mt-1">
                  O cupom será liberado automaticamente após confirmação
                </p>
              </div>

              {/* Botões */}
              <div className="flex gap-2">
                <Button
                  onClick={checkPixStatus}
                  disabled={pixChecking}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="check-pix-btn"
                >
                  {pixChecking ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Verificando...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Verificar Pagamento
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPixModal(false);
                    setPixData(null);
                    setShowPaymentModal(true);
                  }}
                  className="border-rose-300 text-rose-600 hover:bg-rose-50"
                >
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Balança */}
      {showBalancaModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowBalancaModal(false)} />
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Scale className="w-6 h-6 text-amber-500" />
                Conectar Balança
              </h2>
              <button onClick={() => setShowBalancaModal(false)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {!balancaService.isSupported() ? (
              <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 text-rose-700">
                <p className="font-semibold">Navegador não suportado</p>
                <p className="text-sm mt-1">A Web Serial API não está disponível neste navegador. Use Google Chrome ou Microsoft Edge.</p>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Modelo da Balança
                    </label>
                    <select
                      value={balancaTipo}
                      onChange={(e) => setBalancaTipo(e.target.value)}
                      className="w-full h-10 px-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    >
                      <option value="generica">Genérica (Auto-detectar)</option>
                      <option value="toledo_prix">Toledo Prix</option>
                      <option value="filizola">Filizola</option>
                      <option value="urano">Urano</option>
                      <option value="balmak">Balmak</option>
                      <option value="elgin">Elgin</option>
                    </select>
                  </div>
                  
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                    <p className="font-semibold">Instruções:</p>
                    <ol className="list-decimal ml-4 mt-1 space-y-1">
                      <li>Conecte a balança via USB ou adaptador Serial-USB</li>
                      <li>Selecione o modelo da balança</li>
                      <li>Clique em "Conectar" e selecione a porta COM</li>
                    </ol>
                  </div>
                </div>
                
                <div className="flex gap-3 mt-6">
                  <Button
                    onClick={() => setShowBalancaModal(false)}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={conectarBalanca}
                    className="flex-1 bg-amber-500 hover:bg-amber-600"
                  >
                    <Scale className="w-4 h-4 mr-2" />
                    Conectar
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal de Caixa */}
      <CaixaModal
        isOpen={showCaixaModal}
        onClose={() => setShowCaixaModal(false)}
        tipo={tipoCaixaModal}
        caixaAtual={caixaAtual}
        onSuccess={handleCaixaSuccess}
      />

      {/* Modal de Sangria/Suprimento */}
      <SangriaSuprimentoModal
        isOpen={showSangriaModal}
        onClose={() => setShowSangriaModal(false)}
        onSuccess={handleSangriaSuccess}
      />
    </div>
  );
};
